import { createApi } from '@reduxjs/toolkit/query/react';

import { User } from '@typings/user';
import { Show, ShowServerData } from '@typings/show';
import { Participant, Speaker } from '@typings/participant';
import { ChannelServerData } from '@typings/channel';
import { usersSlice } from '@redux/state/usersSlice';
import { SHOWS_PER_PAGE } from '@utils/constants';
import { participantsMapper } from '@redux/helpers/participants';
import { showsSlice } from '@redux/state/showsSlice';
import { feedSlice } from '@redux/state/feedSlice';
import { participantsSlice } from '@redux/state/participantsSlice';
import { channelsSlice } from '@redux/state/channelsSlice';
import { speakersSlice } from '@redux/state/speakersSlice';
import { searchSlice } from '@redux/state/searchSlice';
import { authSlice } from '@redux/state/authSlice';
import { AppState } from '@redux/store';

import { dynamicBaseQuery } from '../baseQuery';

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: dynamicBaseQuery,
  endpoints: (builder) => ({
    getUserData: builder.query<User, void>({
      query: () => ({
        url: '/user',
      }),
      keepUnusedDataFor: 3,
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data?.id) {
            dispatch(authSlice.actions.setUser(data));
            dispatch(usersSlice.actions.setUserDataById(data));
          }
        } catch (error) {
          console.log('getUserData error', error);
        }
      },
    }),
    updateUserData: builder.mutation<User, Partial<User> | { team: number }>({
      query: (body) => ({
        url: '/user',
        method: 'PUT',
        body,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data?.id) {
            dispatch(usersSlice.actions.setUserDataById(data));
            dispatch(authSlice.actions.setUser(data));
          }
        } catch (error) {
          console.log('updateUserData error', error);
        }
      },
    }),
    getUserDataById: builder.query<User, User>({
      query: ({ id }) => ({
        url: `/user/${id}`,
      }),
      keepUnusedDataFor: 3,
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data?.id) {
            dispatch(usersSlice.actions.setUserDataById(data));
          }
        } catch (error) {
          console.log('getUserDataById error', error);
        }
      },
    }),
    getUserSuggestedLiveShows: builder.query<
      { shows: ShowServerData[] },
      {
        fromMyTeam: boolean;
        lastId?: string;
        isRefresh: boolean;
        perPage?: number;
      }
    >({
      query: ({ fromMyTeam, lastId, perPage = SHOWS_PER_PAGE }) => ({
        url: '/user/shows/live',
        params: { lastId, perPage, fromMyTeam },
      }),
      keepUnusedDataFor: 0,
      onQueryStarted: async (
        { fromMyTeam, isRefresh },
        { dispatch, queryFulfilled },
      ) => {
        try {
          const { data } = await queryFulfilled;
          if (data.shows.length) {
            let allParticipantsData: Record<string, Participant> = {};
            let allUsersData: Record<string, User> = {};
            let allSpeakersData: Record<string, Speaker> = {};
            const allChannelsData: ChannelServerData[] = [];
            const reducedShows: Record<string, Show> = data.shows.reduce(
              (acc, show) => {
                const {
                  host,
                  coHost,
                  participants,
                  speakers,
                  currentTopicId,
                  listenersCount,
                  channel,
                  ...restShow
                } = show;
                const {
                  participantsData,
                  usersData,
                  rightTeamSpeakerId,
                  leftTeamSpeakerId,
                  speakersData,
                } = participantsMapper(participants, show.id, speakers);
                allParticipantsData = {
                  ...allParticipantsData,
                  ...participantsData,
                };
                allUsersData = { ...allUsersData, ...usersData };
                allSpeakersData = { ...allSpeakersData, ...speakersData };
                acc[show.id] = {
                  ...restShow,
                  hostId: host.id,
                  coHostId: coHost?.id,
                  currentTopicId,
                  listenersCount: listenersCount || 0,
                  rightTeamSpeakerId,
                  leftTeamSpeakerId,
                  channelId: channel?.id,
                };

                if (channel) {
                  allChannelsData.push(channel);
                }
                return { ...acc };
              },
              {} as Record<string, Show>,
            );

            const suggestedShowsIds: string[] = data.shows.map(({ id }) => id);

            dispatch(
              showsSlice.actions.setShowsData(Object.values(reducedShows)),
            );
            dispatch(
              feedSlice.actions.setFeedSuggestedLiveIds({
                fromMyTeam,
                ids: suggestedShowsIds,
                isRefresh,
              }),
            );
            dispatch(
              participantsSlice.actions.setParticipantsData(
                Object.values(allParticipantsData),
              ),
            );
            dispatch(
              speakersSlice.actions.setSpeakersData(
                Object.values(allSpeakersData),
              ),
            );
            dispatch(
              usersSlice.actions.setUsersData(Object.values(allUsersData)),
            );
            if (allChannelsData.length) {
              dispatch(channelsSlice.actions.setChannelsData(allChannelsData));
            }
          } else {
            dispatch(
              feedSlice.actions.setFeedSuggestedLiveIds({
                fromMyTeam,
                ids: [],
                isRefresh,
              }),
            );
          }
        } catch (error) {
          console.log('getUserSuggestedLiveShows error', error);
        }
      },
    }),
    getUserSuggestedRecordedShows: builder.query<
      { shows: ShowServerData[] },
      {
        lastId?: string;
        isRefresh: boolean;
        perPage?: number;
      }
    >({
      query: ({ lastId, perPage = SHOWS_PER_PAGE }) => ({
        url: '/user/shows/recorded',
        params: { lastId, perPage },
      }),
      keepUnusedDataFor: 0,
      onQueryStarted: async ({ isRefresh }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.shows.length) {
            const usersData: Record<string, User> = {};
            const allChannelsData: ChannelServerData[] = [];
            const reducedShows = data.shows.reduce(
              (acc: Record<string, Show>, show: ShowServerData) => {
                const { host, coHost, listenersCount, channel, ...restShow } =
                  show;
                acc[show.id] = {
                  ...restShow,
                  hostId: host.id,
                  coHostId: coHost?.id,
                  listenersCount: listenersCount || 0,
                  channelId: channel?.id,
                } as unknown as Show;
                usersData[host.id] = {
                  ...host,
                };
                if (coHost && typeof coHost === 'object') {
                  usersData[(coHost as User)?.id] = { ...(coHost as User) };
                }
                if (channel) {
                  allChannelsData.push(channel);
                }
                return { ...acc };
              },
              {},
            );

            const recordedShowsIds: string[] = data.shows.map(({ id }) => id);

            dispatch(
              showsSlice.actions.setShowsData(Object.values(reducedShows)),
            );
            dispatch(usersSlice.actions.setUsersData(Object.values(usersData)));
            if (allChannelsData.length) {
              dispatch(channelsSlice.actions.setChannelsData(allChannelsData));
            }
            dispatch(
              feedSlice.actions.setFeedSuggestedRecordedIds({
                ids: recordedShowsIds,
                isRefresh,
              }),
            );
          } else {
            dispatch(
              feedSlice.actions.setFeedSuggestedRecordedIds({
                ids: [],
                isRefresh,
              }),
            );
          }
        } catch (error) {
          console.log('getUserSuggestedRecordedShows error', error);
        }
      },
    }),
    getUserRecordedShows: builder.query<
      { shows: ShowServerData[] },
      {
        userId: string;
        isMe: boolean;
        lastId?: string;
        isRefresh: boolean;
        perPage?: number;
      }
    >({
      query: ({ userId, lastId, perPage = SHOWS_PER_PAGE }) => ({
        url: `/user/${userId}/recorded-shows`,
        params: { lastId, perPage },
      }),
      keepUnusedDataFor: 3,
      onQueryStarted: async (
        { userId, isMe, isRefresh },
        { dispatch, queryFulfilled },
      ) => {
        try {
          const { data } = await queryFulfilled;
          if (data.shows.length) {
            const reducedShows = data.shows.reduce(
              (acc: Record<string, Show>, show: ShowServerData) => {
                const { host, listenersCount, ...restShow } = show;
                acc[show.id] = {
                  ...restShow,
                  hostId: host.id,
                  listenersCount: listenersCount || 0,
                } as unknown as Show;
                return { ...acc };
              },
              {},
            );

            const recordedShowsIds: string[] = data.shows.map(({ id }) => id);

            dispatch(
              showsSlice.actions.setShowsData(Object.values(reducedShows)),
            );

            if (isMe) {
              dispatch(
                feedSlice.actions.setMyProfileRecordedShowsIds({
                  ids: recordedShowsIds,
                  isRefresh,
                }),
              );
            } else {
              dispatch(
                feedSlice.actions.setRecordedShowsByProfileId({
                  userId,
                  ids: recordedShowsIds,
                  isRefresh,
                }),
              );
            }
          } else {
            if (isMe) {
              dispatch(
                feedSlice.actions.setMyProfileRecordedShowsIds({
                  ids: [],
                  isRefresh,
                }),
              );
            } else {
              dispatch(
                feedSlice.actions.setRecordedShowsByProfileId({
                  userId,
                  ids: [],
                  isRefresh,
                }),
              );
            }
          }
        } catch (error) {
          console.log('getUserRecordedShows error', error);
        }
      },
    }),
    getAllUserShows: builder.query<
      { shows: Show[] },
      { userId: string; lastId?: string; search?: string }
    >({
      query: ({ userId, lastId, search = '' }) => ({
        url: `/user/${userId}/shows`,
        params: { lastId, search },
      }),
      keepUnusedDataFor: 3,
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          await queryFulfilled;
        } catch (error) {
          console.log('getAllUserShows error', error);
        }
      },
    }),
    getAllUserFollowingShows: builder.query<
      { shows: ShowServerData[] },
      {
        lastId?: string;
        isRefresh: boolean;
        perPage?: number;
      }
    >({
      query: ({ lastId, perPage = SHOWS_PER_PAGE }) => ({
        url: '/user/following-shows/live',
        params: { lastId, perPage },
      }),
      keepUnusedDataFor: 0,
      onQueryStarted: async ({ isRefresh }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.shows.length) {
            let allParticipantsData: Record<string, Participant> = {};
            let allUsersData: Record<string, User> = {};
            let allSpeakersData: Record<string, Speaker> = {};
            const allChannelsData: ChannelServerData[] = [];
            const reducedShows: Record<string, Show> = data.shows.reduce(
              (acc, show) => {
                const {
                  host,
                  coHost,
                  participants,
                  speakers,
                  currentTopicId,
                  listenersCount,
                  channel,
                  ...restShow
                } = show;
                const {
                  participantsData,
                  usersData,
                  rightTeamSpeakerId,
                  leftTeamSpeakerId,
                  speakersData,
                } = participantsMapper(participants, show.id, speakers);
                allParticipantsData = {
                  ...allParticipantsData,
                  ...participantsData,
                };
                allUsersData = { ...allUsersData, ...usersData };
                allSpeakersData = { ...allSpeakersData, ...speakersData };
                acc[show.id] = {
                  ...restShow,
                  hostId: host.id,
                  coHostId: coHost?.id,
                  currentTopicId,
                  listenersCount: listenersCount || 0,
                  rightTeamSpeakerId,
                  leftTeamSpeakerId,
                  channelId: channel?.id,
                };
                if (channel) {
                  allChannelsData.push(channel);
                }
                return { ...acc };
              },
              {} as Record<string, Show>,
            );

            const followingShowsIds: string[] = data.shows.map(({ id }) => id);

            dispatch(
              showsSlice.actions.setShowsData(Object.values(reducedShows)),
            );
            dispatch(
              participantsSlice.actions.setParticipantsData(
                Object.values(allParticipantsData),
              ),
            );
            dispatch(
              speakersSlice.actions.setSpeakersData(
                Object.values(allSpeakersData),
              ),
            );
            dispatch(
              usersSlice.actions.setUsersData(Object.values(allUsersData)),
            );
            if (allChannelsData.length) {
              dispatch(channelsSlice.actions.setChannelsData(allChannelsData));
            }
            dispatch(
              feedSlice.actions.setFeedSFollowingIds({
                block: 'live',
                ids: followingShowsIds,
                isRefresh,
              }),
            );
          } else {
            dispatch(
              feedSlice.actions.setFeedSFollowingIds({
                block: 'live',
                ids: [],
                isRefresh,
              }),
            );
          }
        } catch (error) {
          console.log('getAllUserFollowingShows error', error);
        }
      },
    }),
    getAllUserFollowingRecordedShows: builder.query<
      { shows: ShowServerData[] },
      {
        lastId?: string;
        isRefresh: boolean;
        perPage?: number;
      }
    >({
      query: ({ lastId, perPage = SHOWS_PER_PAGE }) => ({
        url: '/user/following-shows/recorded',
        params: { lastId, perPage },
      }),
      keepUnusedDataFor: 0,
      onQueryStarted: async ({ isRefresh }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.shows.length) {
            const usersData: Record<string, User> = {};
            const allChannelsData: ChannelServerData[] = [];
            const reducedShows = data.shows.reduce(
              (acc: Record<string, Show>, show: ShowServerData) => {
                const { host, coHost, listenersCount, channel, ...restShow } =
                  show;
                acc[show.id] = {
                  ...restShow,
                  hostId: host.id,
                  coHostId: coHost?.id,
                  listenersCount: listenersCount || 0,
                  channelId: channel?.id,
                } as unknown as Show;
                usersData[host.id] = {
                  ...host,
                };
                if (coHost && typeof coHost === 'object') {
                  usersData[(coHost as User)?.id] = { ...(coHost as User) };
                }
                if (channel) {
                  allChannelsData.push(channel);
                }
                return { ...acc };
              },
              {},
            );

            const recordedShowsIds: string[] = data.shows.map(({ id }) => id);

            dispatch(
              showsSlice.actions.setShowsData(Object.values(reducedShows)),
            );
            dispatch(usersSlice.actions.setUsersData(Object.values(usersData)));
            if (allChannelsData.length) {
              dispatch(channelsSlice.actions.setChannelsData(allChannelsData));
            }
            dispatch(
              feedSlice.actions.setFeedSFollowingIds({
                block: 'recorded',
                ids: recordedShowsIds,
                isRefresh,
              }),
            );
          } else {
            dispatch(
              feedSlice.actions.setFeedSFollowingIds({
                block: 'recorded',
                ids: [],
                isRefresh,
              }),
            );
          }
        } catch (error) {
          console.log('getAllUserFollowingRecordedShows error', error);
        }
      },
    }),
    getScheduledShows: builder.query<
      ShowServerData[],
      {
        perPage?: number;
        isRefresh: boolean;
        lastId?: string;
      }
    >({
      query: ({ perPage = 10, lastId }) => {
        return {
          url: '/scheduled-shows',
          params: { perPage, lastId },
          method: 'GET',
        };
      },
      keepUnusedDataFor: 0,
      onQueryStarted: async ({ isRefresh }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.length) {
            let allParticipantsData: Record<string, Participant> = {};
            let allUsersData: Record<string, User> = {};
            const allChannelsData: ChannelServerData[] = [];
            const reducedShows: Record<string, Show> = data.reduce(
              (acc, show) => {
                const { host, coHost, participants, channel, ...restShow } =
                  show;
                const {
                  participantsData,
                  usersData,
                  rightTeamSpeakerId,
                  leftTeamSpeakerId,
                } = participantsMapper(participants, show.id);
                allParticipantsData = {
                  ...allParticipantsData,
                  ...participantsData,
                };
                allUsersData = { ...allUsersData, ...usersData };
                acc[show.id] = {
                  ...restShow,
                  hostId: host.id,
                  coHostId: coHost?.id,
                  listenersCount: 0,
                  rightTeamSpeakerId,
                  leftTeamSpeakerId,
                  channelId: channel?.id,
                };
                if (channel) {
                  allChannelsData.push(channel);
                }
                return { ...acc };
              },
              {} as Record<string, Show>,
            );

            const ids: string[] = data.map(({ id }) => id);

            dispatch(
              showsSlice.actions.setShowsData(Object.values(reducedShows)),
            );
            dispatch(
              participantsSlice.actions.setParticipantsData(
                Object.values(allParticipantsData),
              ),
            );
            dispatch(
              usersSlice.actions.setUsersData(Object.values(allUsersData)),
            );
            if (allChannelsData.length) {
              dispatch(channelsSlice.actions.setChannelsData(allChannelsData));
            }
            dispatch(
              feedSlice.actions.setScheduledShowsIds({
                ids,
                isRefresh,
              }),
            );
            return;
          }

          dispatch(
            feedSlice.actions.setScheduledShowsIds({ ids: [], isRefresh }),
          );
        } catch (error) {
          console.log('getScheduledShows error', error);
          dispatch(
            feedSlice.actions.setScheduledShowsIds({ ids: [], isRefresh }),
          );
        }
      },
    }),
    getAllUsers: builder.query<
      User[],
      {
        perPage: number;
        isRefresh: boolean;
        lastId?: string;
        filters?: string[];
        search?: string;
      }
    >({
      query: ({ perPage, filters, search, lastId }) => {
        return {
          url: '/users',
          params: { perPage, filters, search: search?.trim(), lastId },
          method: 'GET',
        };
      },
      keepUnusedDataFor: 3,
      onQueryStarted: async ({ isRefresh }, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;

          if (!data.length) {
            dispatch(searchSlice.actions.setUsersIds({ ids: [], isRefresh }));
            return;
          }

          dispatch(
            searchSlice.actions.setUsersIds({
              ids: data.map(({ id }) => id),
              isRefresh,
            }),
          );
          dispatch(usersSlice.actions.setUsersData(data));
        } catch (error) {
          console.log('getAllUsers error', error);
          dispatch(searchSlice.actions.setUsersIds({ ids: [], isRefresh }));
        }
      },
    }),
    generateUserAvatar: builder.mutation<User, { photo: File; gender: string }>(
      {
        query: ({ photo, gender }) => {
          const bodyFormData = new FormData();
          bodyFormData.append(
            'photo',
            photo,
            `${new Date().getTime()}-${photo.name}`,
          );
          bodyFormData.append('gender', gender);
          return {
            url: '/user/avatar',
            method: 'POST',
            body: bodyFormData,
          };
        },
        onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
          try {
            const { data } = await queryFulfilled;
            if (data?.id) {
              dispatch(usersSlice.actions.setUserDataById(data));
            }
          } catch (error) {
            console.log('generateUserAvatar error', error);
          }
        },
      },
    ),
    updateUserSettings: builder.mutation<void, { usernameColor: string }>({
      query: (body) => ({
        url: '/user-settings',
        method: 'POST',
        body,
      }),
      onQueryStarted: async (
        { usernameColor },
        { dispatch, queryFulfilled, getState },
      ) => {
        try {
          await queryFulfilled;
          const { auth } = getState() as AppState;
          if (auth.user?.id) {
            dispatch(
              usersSlice.actions.setUserDataById({
                id: auth.user?.id,
                usernameColor,
              }),
            );
          }
        } catch (error) {
          console.log('updateUserSettings error', error);
        }
      },
    }),
  }),
});

export const {
  useLazyGetUserDataQuery,
  useUpdateUserDataMutation,
  useLazyGetUserDataByIdQuery,
  useLazyGetUserSuggestedLiveShowsQuery,
  useLazyGetUserSuggestedRecordedShowsQuery,
  useLazyGetUserRecordedShowsQuery,
  useLazyGetAllUserShowsQuery,
  useLazyGetAllUserFollowingShowsQuery,
  useLazyGetAllUserFollowingRecordedShowsQuery,
  useLazyGetScheduledShowsQuery,
  useLazyGetAllUsersQuery,
  useGenerateUserAvatarMutation,
  useUpdateUserSettingsMutation,
} = userApi;
